import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useQueryWithErrorHandler } from './useQueryWithErrorHandler';
import {
  IResponseData,
  IError,
  IApiParams,
  IDosageEntity,
  IDosagePropertyEntity,
  IDosagePayload,
  IDosageEntityFull,
  IAssignDosagePayload,
  IAddDosagePropertyValuePayload,
  IAddDosageBatchItemPayload,
  IAssignedDosages,
} from 'types';
import { useError } from '../providers';
import { useAuthClient } from './useAuthClient';

const dosagesQueryKey = 'getDosages';
const dosageQueryKey = 'getDosage';
const assignedDosagesQueryKey = 'getAssignedDosages';

const dosagesPropertiesQueryKey = 'getDosagesProperties';
const createDosageQueryKey = 'createDosage';
const removeDosageFromNotificationQueryKey = 'removeDosageFromNotification';
const assignDosageToNotificationQueryKey = 'assignDosageToNotification';
const addDosagePropertyValueQueryKey = 'addDosagePropertyValue';
const removeDosagePropertyValueQueryKey = 'removeDosagePropertyValue';
const updateDosagePropertyValueQueryKey = 'updateDosagePropertyValue';
const addDosageBatchItemQueryKey = 'addDosageBatchItem';
const updateDosageBatchItemQueryKey = 'updateDosageBatchItem';
const removeDosageBatchItemQueryKey = 'removeDosageBatchItem';
const updateDosageCementOnlyQueryKey = 'updateDosageCementOnly';

export function useDosagesApi({ queryParameters, organizationId }: IApiParams) {
  const authClient = useAuthClient();
  const { openModal } = useError();
  const queryClient = useQueryClient();

  const getDosagesUrl = useMemo(() => {
    return `/api/v1/${organizationId}/Dosage/search-dosages`;
  }, [organizationId]);
  const getDosageUrl = useMemo(() => {
    return `/api/v1/${organizationId}/Dosage/get-dosage`;
  }, [organizationId]);
  const getAssignedDosageUrl = useMemo(() => {
    return `/api/v1/${organizationId}/Dosage/get-dosages`;
  }, [organizationId]);
  const getDosagesPropertiesUrl = useMemo(() => {
    return `/api/v1/${organizationId}/Dosage/get-dosage-properties`;
  }, [organizationId]);
  const createDosageUrl = useMemo(() => {
    return `/api/v1/${organizationId}/Dosage/create-dosage`;
  }, [organizationId]);
  const removeDosageFromNotificationEntityUrl = useMemo(() => {
    return `/api/v1/${organizationId}/Dosage/delete-dosage-from-notification-entity`;
  }, [organizationId]);
  const assignDosageToNotificationEntityUrl = useMemo(() => {
    return `/api/v1/${organizationId}/Dosage/add-dosage-to-notification-entity`;
  }, [organizationId]);
  const assignDosagePropertyToDosage = useMemo(() => {
    return `/api/v1/${organizationId}/Dosage/add-dosage-property-value`;
  }, [organizationId]);
  const removeDosagePropertyToDosageUrl = useMemo(() => {
    return `/api/v1/${organizationId}/Dosage/delete-dosage-property-value`;
  }, [organizationId]);
  const updateDosagePropertyToDosageUrl = useMemo(() => {
    return `/api/v1/${organizationId}/Dosage/update-dosage-property-value`;
  }, [organizationId]);
  const addDosageBatchItemUrl = useMemo(() => {
    return `/api/v1/${organizationId}/Dosage/add-dosage-batch-item`;
  }, [organizationId]);
  const updateDosageBatchItemUrl = useMemo(() => {
    return `/api/v1/${organizationId}/Dosage/update-dosage-batch-item-ranges`;
  }, [organizationId]);
  const removeDosageBatchItemUrl = useMemo(() => {
    return `/api/v1/${organizationId}/Dosage/delete-dosage-batch-item`;
  }, [organizationId]);
  const updateDosageCementOnlyUrl = useMemo(() => {
    return `/api/v1/${organizationId}/Dosage/update-dosage-cement-only`;
  }, [organizationId]);
  const {
    mutateAsync: updateDosagePropertyValue,
    isPending: isUpdateDosagePropertyValue,
  } = useMutation({
    mutationFn: (
      payload: IAddDosagePropertyValuePayload,
    ): Promise<IResponseData<boolean>> =>
      authClient(updateDosagePropertyToDosageUrl, {
        method: 'PUT',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [updateDosagePropertyValueQueryKey],
      });
      return queryClient.getQueryData<IResponseData<any>>([
        updateDosagePropertyValueQueryKey,
      ]);
    },

    onError: (error: IError) => {
      openModal(error);
    },
  });

  const {
    mutateAsync: updateDosageBatchItem,
    isPending: isUpdateDosageBatchItem,
  } = useMutation({
    mutationFn: (
      payload: IAddDosageBatchItemPayload,
    ): Promise<IResponseData<boolean>> =>
      authClient(updateDosageBatchItemUrl, {
        method: 'PUT',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [updateDosageBatchItemQueryKey],
      });
      return queryClient.getQueryData<IResponseData<any>>([
        updateDosageBatchItemQueryKey,
      ]);
    },

    onError: (error: IError) => {
      openModal(error);
    },
  });

  const {
    mutateAsync: updateDosageCementOnly,
    isPending: isUpdatingDosageCementOnly,
  } = useMutation({
    mutationFn: (payload: {
      dosageId: string;
      cementOnly: boolean;
    }): Promise<IResponseData<boolean>> =>
      authClient(updateDosageCementOnlyUrl, {
        method: 'PUT',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [updateDosageCementOnlyQueryKey],
      });
      return queryClient.getQueryData<IResponseData<any>>([
        updateDosageCementOnlyQueryKey,
      ]);
    },

    onError: (error: IError) => {
      openModal(error);
    },
  });

  const {
    mutateAsync: removeDosagePropertyValue,
    isPending: isRemovingDosagePropertyValue,
  } = useMutation({
    mutationFn: (
      payload: IAddDosagePropertyValuePayload,
    ): Promise<IResponseData<boolean>> =>
      authClient(removeDosagePropertyToDosageUrl, {
        method: 'DELETE',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [removeDosagePropertyValueQueryKey],
      });
      return queryClient.getQueryData<IResponseData<any>>([
        removeDosagePropertyValueQueryKey,
      ]);
    },

    onError: (error: IError) => {
      openModal(error);
    },
  });

  const {
    mutateAsync: removeDosageBatchItem,
    isPending: isRemovingDosageBatchItem,
  } = useMutation({
    mutationFn: (
      payload: IAddDosageBatchItemPayload,
    ): Promise<IResponseData<boolean>> =>
      authClient(removeDosageBatchItemUrl, {
        method: 'DELETE',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [removeDosageBatchItemQueryKey],
      });
      return queryClient.getQueryData<IResponseData<any>>([
        removeDosageBatchItemQueryKey,
      ]);
    },

    onError: (error: IError) => {
      openModal(error);
    },
  });

  const {
    mutateAsync: assignDosageToNotificationEntity,
    isPending: isAssigningDosageToNotificationEntity,
  } = useMutation({
    mutationFn: (
      payload: IAssignDosagePayload,
    ): Promise<IResponseData<boolean>> =>
      authClient(assignDosageToNotificationEntityUrl, {
        method: 'POST',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [assignDosageToNotificationQueryKey],
      });
      return queryClient.getQueryData<IResponseData<any>>([
        assignDosageToNotificationQueryKey,
      ]);
    },

    onError: (error: IError) => {
      openModal(error);
    },
  });

  const {
    mutateAsync: addDosagePropertyValue,
    isPending: isAddingDosagePropertyValue,
  } = useMutation({
    mutationFn: (
      payload: IAddDosagePropertyValuePayload,
    ): Promise<IResponseData<boolean>> =>
      authClient(assignDosagePropertyToDosage, {
        method: 'POST',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [addDosagePropertyValueQueryKey],
      });
      return queryClient.getQueryData<IResponseData<any>>([
        addDosagePropertyValueQueryKey,
      ]);
    },

    onError: (error: IError) => {
      openModal(error);
    },
  });

  const {
    mutateAsync: addDosageBatchItem,
    isPending: isAddingDosageBatchItem,
  } = useMutation({
    mutationFn: (
      payload: IAddDosageBatchItemPayload,
    ): Promise<IResponseData<boolean>> =>
      authClient(addDosageBatchItemUrl, {
        method: 'POST',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [addDosageBatchItemQueryKey],
      });
      return queryClient.getQueryData<IResponseData<any>>([
        addDosageBatchItemQueryKey,
      ]);
    },

    onError: (error: IError) => {
      openModal(error);
    },
  });

  const { mutateAsync: createDosage, isPending: isCreatingDosage } =
    useMutation({
      mutationFn: (
        payload: IDosagePayload,
      ): Promise<IResponseData<IDosageEntity>> =>
        authClient(createDosageUrl, {
          method: 'POST',
          data: payload,
        }),

      retry: false,

      onMutate: async () => {
        await queryClient.cancelQueries({
          queryKey: [createDosageQueryKey],
        });
        return queryClient.getQueryData<IResponseData<any>>([
          createDosageQueryKey,
        ]);
      },

      onError: (error: IError) => {
        openModal(error);
      },
    });

  const useDosagesQuery = (queryKey: string) => {
    return useQueryWithErrorHandler<IResponseData<IDosageEntity[]>, IError>({
      queryKey: [queryKey, { queryParameters }],

      queryFn: async () => {
        return await authClient(`${getDosagesUrl}`, {
          queryParameters: {
            DosageName: queryParameters?.query,
          },
          method: 'GET',
        });
      },

      enabled: false,
      refetchOnWindowFocus: false,

      onError: error => {
        openModal(error);
      },
    });
  };

  const {
    data: dosage,
    mutateAsync: fetchDosage,
    isPending: isFetchingDosage,
    reset: clearDosage,
  } = useMutation({
    mutationFn: (payload: {
      dosageId?: string;
    }): Promise<IResponseData<IDosageEntityFull>> =>
      authClient(getDosageUrl, {
        method: 'GET',
        queryParameters: payload,
      }),

    retry: false,

    onMutate: async variables => {
      await queryClient.cancelQueries({
        queryKey: [dosageQueryKey, variables.dosageId],
      });
      return queryClient.getQueryData<IResponseData<any>>([
        dosageQueryKey,
        variables.dosageId,
      ]);
    },
  });

  const {
    data: assignedDosages,
    mutateAsync: fetchAssignedDosages,
    isPending: isFetchingAssignedDosages,
    reset: clearAssignedDosages,
  } = useMutation({
    mutationFn: (payload: {
      notificationEntityId?: string;
    }): Promise<IResponseData<IAssignedDosages[]>> =>
      authClient(getAssignedDosageUrl, {
        method: 'GET',
        queryParameters: payload,
      }),

    retry: false,

    onMutate: async variables => {
      await queryClient.cancelQueries({
        queryKey: [assignedDosagesQueryKey, variables.notificationEntityId],
      });
      return queryClient.getQueryData<IResponseData<any>>([
        assignedDosagesQueryKey,
        variables.notificationEntityId,
      ]);
    },
  });

  const useDosagesPropertiesQuery = (queryKey: string) => {
    return useQueryWithErrorHandler<
      IResponseData<IDosagePropertyEntity[]>,
      IError
    >({
      queryKey: [queryKey, { queryParameters }],

      queryFn: async () => {
        return await authClient(`${getDosagesPropertiesUrl}`, {
          method: 'GET',
        });
      },

      enabled: false,
      refetchOnWindowFocus: false,

      onError: error => {
        openModal(error);
      },
    });
  };

  const {
    refetch: removeDosageFromEntity,
    isFetching: isRemovingDosageFromEntity,
  } = useQueryWithErrorHandler<IResponseData<boolean>, IError>({
    queryKey: [removeDosageFromNotificationQueryKey],

    queryFn: async () => {
      return await authClient(`${removeDosageFromNotificationEntityUrl}`, {
        queryParameters: {
          DosageId: queryParameters?.dosageId,
          NotificationEntityId: queryParameters?.notificationEntityId,
          NotificationEntityType: queryParameters?.notificationEntityType,
        },
        method: 'DELETE',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,
    retry: false,

    onError: (error: IError) => {
      openModal(error);
    },
  });

  const {
    data: dosages,
    refetch: fetchDosages,
    isFetching: isFetchingDosages,
  } = useDosagesQuery(dosagesQueryKey);

  const {
    data: dosagesProperties,
    refetch: fetchDosagesProperties,
    isFetching: isFetchingDosagesProperties,
  } = useDosagesPropertiesQuery(dosagesPropertiesQueryKey);

  return {
    dosages,
    fetchDosages,
    isFetchingDosages,
    dosagesProperties,
    fetchDosagesProperties,
    isFetchingDosagesProperties,
    dosage,
    fetchDosage,
    isFetchingDosage,
    createDosage,
    isCreatingDosage,
    removeDosageFromEntity,
    isRemovingDosageFromEntity,
    assignDosageToNotificationEntity,
    isAssigningDosageToNotificationEntity,
    clearDosage,
    addDosagePropertyValue,
    isAddingDosagePropertyValue,
    removeDosagePropertyValue,
    isRemovingDosagePropertyValue,
    updateDosagePropertyValue,
    isUpdateDosagePropertyValue,
    addDosageBatchItem,
    isAddingDosageBatchItem,
    updateDosageBatchItem,
    isUpdateDosageBatchItem,
    removeDosageBatchItem,
    isRemovingDosageBatchItem,
    assignedDosages,
    fetchAssignedDosages,
    isFetchingAssignedDosages,
    clearAssignedDosages,
    updateDosageCementOnly,
    isUpdatingDosageCementOnly,
  };
}
